import RAPIER from "@dimforge/rapier3d-compat";
import {
  BODY_MUTATORS, BODY_STRIDE, COLLIDER_MUTATORS, Command, FromWorker, RigidMotion, ToWorker, WORLD_SETTERS,
  applyRigidMotion, copyArg, serializeBodyDesc, serializeColliderDesc,
} from "./physics_protocol.js";

type Fn = ( ...args: unknown[] ) => unknown;
type Methods = Record<string, Fn>;

type ContactEntry = { other: number; nx: number; ny: number; nz: number; points: number[] };
type ColliderMeta = { parent: number | null; ox: number; oy: number; oz: number; radius: number };

class ContactView
{
  constructor( private readonly entry: ContactEntry, private readonly flipped: boolean ) {}

  public numSolverContacts(): number
  {
    return this.entry.points.length / 4;
  }

  public numContacts(): number
  {
    return this.numSolverContacts();
  }

  public solverContactPoint( i: number ): { x: number; y: number; z: number }
  {
    const p = this.entry.points;
    return { x: p[i * 4], y: p[i * 4 + 1], z: p[i * 4 + 2] };
  }

  public solverContactDist( i: number ): number
  {
    return this.entry.points[i * 4 + 3];
  }

  public normal(): { x: number; y: number; z: number }
  {
    const s = this.flipped ? -1 : 1;
    return { x: this.entry.nx * s, y: this.entry.ny * s, z: this.entry.nz * s };
  }
}

const RAY_MARGIN = 0.35;
const POSE_SIZE = BODY_STRIDE + 4;
const STAMP = BODY_STRIDE;
const UNSYNCED = BODY_STRIDE + 1;
const NATIVE = BODY_STRIDE + 2;
const LISTED = BODY_STRIDE + 3;
const STALL_MS = 500;
const REPLAY_SKIP = new Set( [ "addForce", "addTorque", "addForceAtPoint" ] );
const STEP_TIMEOUT_MS = 3000;

export class PhysicsThread
{
  private static installed = false;
  private static world: RAPIER.World | null = null;
  private static worker: Worker | null = null;
  private static active = false;
  private static recording = false;
  private static dirty = true;
  private static seq = 0;
  private static reset_seq = 0;
  private static in_flight = -1;
  private static sent_at = 0;
  private static cmds: Command[] = [];
  private static latest: Extract<FromWorker, { type: "state" }> | null = null;
  private static poses = new Map<number, Float64Array>();
  private static contacts = new Map<number, ContactEntry[]>();
  private static tracked = new Set<number>();
  private static to_track: number[] = [];
  private static to_untrack: number[] = [];
  private static meta = new Map<number, ColliderMeta>();
  private static needs_propagate = false;
  private static unsynced: number[] = [];
  private static stamp = 0;
  private static ray_epoch = 0;
  private static ray_cache_epoch = -1;
  private static ray_count = 0;
  private static ray_handles = new Float64Array( 0 );
  private static ray_parents = new Float64Array( 0 );
  private static ray_cx = new Float64Array( 0 );
  private static ray_cy = new Float64Array( 0 );
  private static ray_cz = new Float64Array( 0 );
  private static ray_r = new Float64Array( 0 );

  private static body_orig: Methods = {};
  private static collider_orig: Methods = {};
  private static world_orig: Methods = {};
  private static read_orig: Methods = {};

  public static step_ms = 0;
  public static last_error = "";

  private static readonly vec = { x: 0, y: 0, z: 0 };
  private static readonly quat = { x: 0, y: 0, z: 0, w: 1 };
  private static readonly touched = new Set<number>();
  private static readonly center_tmp = { x: 0, y: 0, z: 0 };

  public static debugState(): Record<string, unknown>
  {
    return {
      active: this.active,
      dirty: this.dirty,
      worker: !!this.worker,
      in_flight: this.in_flight,
      pending_cmds: this.cmds.length,
      tracked: this.tracked.size,
      unsynced: this.unsynced.length,
      poses: this.poses.size,
      step_ms: +this.step_ms.toFixed( 2 ),
      last_error: this.last_error,
    };
  }

  public static syncMirror(): void
  {
    if ( this.world && this.active && !this.dirty )
      this.syncAll();
  }

  public static isActive(): boolean
  {
    return this.active;
  }

  private static last_can_step = 0;

  public static canStep(): boolean
  {
    if ( !this.active )
      return false;
    const now = performance.now();
    if ( now - this.last_can_step > STALL_MS )
      this.sent_at = Math.max( this.sent_at, now );
    this.last_can_step = now;
    if ( this.in_flight >= 0 && now - this.sent_at > STEP_TIMEOUT_MS )
      this.fail( "worker did not answer a step" );
    return this.active && this.in_flight < 0;
  }

  public static start( world: RAPIER.World, url: URL ): void
  {
    this.world = world;
    this.install();

    let worker: Worker;
    try
    {
      worker = new Worker( url, { type: "module" } );
    }
    catch ( error )
    {
      this.fail( `worker: ${String( error )}` );
      return;
    }

    this.worker = worker;
    worker.onerror = event => this.fail( `worker error: ${event.message}` );
    worker.onmessage = ( event: MessageEvent<FromWorker> ) => this.receive( event.data );
  }

  public static stop(): void
  {
    if ( this.world && this.active && !this.dirty )
      this.syncAll();
    this.worker?.terminate();
    this.worker = null;
    this.active = false;
    this.recording = false;
    this.in_flight = -1;
    this.latest = null;
    this.contacts.clear();
  }

  public static markDirty(): void
  {
    this.ray_epoch++;
    const was_live = !!this.world && !this.dirty;
    if ( was_live )
      this.syncAll();
    this.dirty = true;
    this.recording = false;
    if ( was_live )
    {
      if ( this.in_flight >= 0 )
        this.applyMoves( this.in_flight_moves );
      this.applyMoves( this.cmds );
    }
    this.in_flight_moves = [];
    this.cmds.length = 0;
    this.latest = null;
    this.in_flight = -1;
    this.reset_seq = this.seq + 1;
    this.contacts.clear();
  }

  public static beginStep(): void
  {
    if ( !this.dirty || !this.worker || !this.world )
      return;

    this.seq++;
    this.reset_seq = this.seq;
    this.in_flight = -1;
    this.latest = null;
    this.cmds.length = 0;
    this.contacts.clear();
    this.to_track.length = 0;
    this.to_untrack.length = 0;
    for ( const h of this.tracked )
      this.to_track.push( h );
    this.syncAll();
    this.refreshPoses();
    if ( !this.post( { type: "reset", seq: this.seq, snapshot: this.world.takeSnapshot() } ) )
      return;
    this.dirty = false;
    this.recording = true;
  }

  public static sendStep( dt: number, substeps = 1 ): void
  {
    if ( this.dirty || !this.active )
      return;
    this.seq++;
    const cmds = this.cmds;
    this.cmds = [];
    this.in_flight_moves = cmds.filter( cmd => cmd.o === "mv" );
    if ( !this.post( { type: "step", seq: this.seq, dt, substeps, cmds, track: this.to_track.splice( 0 ), untrack: this.to_untrack.splice( 0 ) } ) )
      return;
    this.in_flight = this.seq;
    this.sent_at = performance.now();
  }

  public static pump(): boolean
  {
    const state = this.latest;
    if ( !state || !this.world || this.dirty )
      return false;

    this.latest = null;
    this.step_ms = state.step_ms;
    this.applyBodies( state.bodies );
    this.readContacts( state.contacts );
    this.replay();
    this.needs_propagate = true;
    return true;
  }

  private static post( message: ToWorker ): boolean
  {
    try
    {
      if ( message.type === "reset" )
        this.worker!.postMessage( message, [ message.snapshot.buffer ] );
      else
        this.worker!.postMessage( message );
      return true;
    }
    catch ( error )
    {
      this.fail( `postMessage: ${String( error )}` );
      return false;
    }
  }

  private static receive( message: FromWorker ): void
  {
    if ( message.type === "ready" )
    {
      this.active = true;
      this.markDirty();
      return;
    }

    if ( message.type === "error" )
    {
      this.fail( message.message );
      return;
    }

    if ( message.seq < this.reset_seq || message.seq !== this.in_flight )
      return;

    this.in_flight = -1;
    this.latest = message;
  }

  private static fail( reason: string ): void
  {
    this.last_error = reason;
    console.error( "[PhysicsThread] falling back to the main thread:", reason );
    this.stop();
  }

  private static ownsBody( body: RAPIER.RigidBody ): boolean
  {
    return !!this.world && ( body as unknown as { rawSet: unknown } ).rawSet === ( this.world.bodies as unknown as { raw: unknown } ).raw;
  }

  private static ownsCollider( collider: RAPIER.Collider ): boolean
  {
    return !!this.world && ( collider as unknown as { colliderSet: unknown } ).colliderSet === this.world.colliders;
  }

  private static body( handle: number ): RAPIER.RigidBody | null
  {
    const body = this.world!.getRigidBody( handle );
    return body && body.handle === handle ? body : null;
  }

  private static collider( handle: number ): RAPIER.Collider | null
  {
    const collider = this.world!.getCollider( handle );
    return collider && collider.handle === handle ? collider : null;
  }

  private static applyBodies( bodies: Float64Array ): void
  {
    const touched = this.touched;
    touched.clear();
    for ( const cmd of this.cmds )
      if ( cmd.o === "b" )
        touched.add( cmd.h );
    const stamp = ++this.stamp;
    this.ray_epoch++;
    if ( this.unsynced.length > 2 * this.poses.size + 64 )
      this.compactUnsynced();

    for ( let i = 0; i < bodies.length; i += BODY_STRIDE )
    {
      const handle = bodies[i];
      const body = this.body( handle );
      if ( !body )
        continue;

      let pose = this.poses.get( handle );
      const is_touched = touched.has( handle );
      if ( pose && !is_touched && pose[NATIVE] === 0 )
      {
        let same = true;
        for ( let k = 1; k < BODY_STRIDE; k++ )
          if ( pose[k] !== bodies[i + k] )
          {
            same = false;
            break;
          }
        if ( same )
        {
          pose[STAMP] = stamp;
          continue;
        }
      }
      if ( !pose )
      {
        pose = new Float64Array( POSE_SIZE );
        this.poses.set( handle, pose );
      }
      for ( let k = 0; k < BODY_STRIDE; k++ )
        pose[k] = bodies[i + k];
      pose[STAMP] = stamp;

      if ( is_touched )
      {
        this.writeMirror( body, pose );
        pose[UNSYNCED] = 0;
        pose[NATIVE] = 1;
      }
      else
      {
        pose[NATIVE] = 0;
        pose[UNSYNCED] = 1;
        if ( pose[LISTED] !== 1 )
        {
          pose[LISTED] = 1;
          this.unsynced.push( handle );
        }
      }
    }
  }

  private static writeMirror( body: RAPIER.RigidBody, pose: Float64Array ): void
  {
    const o = this.body_orig;
    const vec = this.vec;
    const quat = this.quat;
    vec.x = pose[1]; vec.y = pose[2]; vec.z = pose[3];
    o.setTranslation.call( body, vec, false );
    quat.x = pose[4]; quat.y = pose[5]; quat.z = pose[6]; quat.w = pose[7];
    o.setRotation.call( body, quat, false );
    this.needs_propagate = true;
    const flags = pose[14];
    if ( Number.isNaN( flags ) || ( flags & 2 ) )
      return;
    vec.x = pose[8]; vec.y = pose[9]; vec.z = pose[10];
    o.setLinvel.call( body, vec, false );
    vec.x = pose[11]; vec.y = pose[12]; vec.z = pose[13];
    o.setAngvel.call( body, vec, false );
    const sleeping = ( flags & 1 ) === 1;
    if ( sleeping !== this.read_orig.isSleeping.call( body ) )
      ( sleeping ? o.sleep : o.wakeUp ).call( body );
  }

  private static ensureMirror( body: RAPIER.RigidBody ): void
  {
    const pose = this.poses.get( body.handle );
    if ( !pose || pose[UNSYNCED] !== 1 )
      return;
    pose[UNSYNCED] = 0;
    this.writeMirror( body, pose );
  }

  private static compactUnsynced(): void
  {
    let n = 0;
    for ( const handle of this.unsynced )
    {
      const pose = this.poses.get( handle );
      if ( pose && pose[UNSYNCED] === 1 )
        this.unsynced[n++] = handle;
      else if ( pose )
        pose[LISTED] = 0;
    }
    this.unsynced.length = n;
  }

  private static syncAll(): void
  {
    for ( const handle of this.unsynced )
    {
      const pose = this.poses.get( handle );
      if ( !pose )
        continue;
      pose[LISTED] = 0;
      if ( pose[UNSYNCED] !== 1 )
        continue;
      pose[UNSYNCED] = 0;
      const body = this.body( handle );
      if ( body )
        this.writeMirror( body, pose );
    }
    this.unsynced.length = 0;
    this.propagate();
  }

  private static propagate(): void
  {
    if ( !this.needs_propagate || !this.world )
      return;
    this.world_orig.propagateModifiedBodyPositionsToColliders.call( this.world );
    this.needs_propagate = false;
  }

  private static cached( body: RAPIER.RigidBody ): Float64Array | null
  {
    if ( !this.active || this.dirty || !this.ownsBody( body ) )
      return null;
    const pose = this.poses.get( body.handle );
    if ( !pose || pose[NATIVE] === 1 || Number.isNaN( pose[14] ) )
      return null;
    return pose;
  }

  private static in_flight_moves: Command[] = [];

  private static applyMoves( cmds: readonly Command[] ): void
  {
    this.ray_epoch++;
    for ( const cmd of cmds )
    {
      if ( cmd.o !== "mv" )
        continue;
      for ( const h of cmd.h )
      {
        const body = this.body( h );
        if ( body )
          applyRigidMotion( body, cmd.m );
      }
      this.needs_propagate = true;
    }
    this.propagate();
  }

  public static moveBodies( world: RAPIER.World, bodies: readonly RAPIER.RigidBody[], motion: RigidMotion ): boolean
  {
    if ( !this.recording || world !== this.world )
      return false;
    const handles = new Array<number>( bodies.length );
    for ( let i = 0; i < bodies.length; i++ )
      handles[i] = bodies[i].handle;
    this.cmds.push( { o: "mv", h: handles, m: { ...motion } } );
    return true;
  }

  public static readTranslation( body: RAPIER.RigidBody, out: { x: number; y: number; z: number } ): void
  {
    const p = this.cached( body );
    if ( p )
    {
      out.x = p[1]; out.y = p[2]; out.z = p[3];
      return;
    }
    const t = body.translation();
    out.x = t.x; out.y = t.y; out.z = t.z;
  }

  public static readRotation( body: RAPIER.RigidBody, out: { x: number; y: number; z: number; w: number } ): void
  {
    const p = this.cached( body );
    if ( p )
    {
      out.x = p[4]; out.y = p[5]; out.z = p[6]; out.w = p[7];
      return;
    }
    const r = body.rotation();
    out.x = r.x; out.y = r.y; out.z = r.z; out.w = r.w;
  }

  public static readLinvel( body: RAPIER.RigidBody, out: { x: number; y: number; z: number } ): void
  {
    const p = this.cached( body );
    if ( p && !( p[14] & 2 ) )
    {
      out.x = p[8]; out.y = p[9]; out.z = p[10];
      return;
    }
    const v = body.linvel();
    out.x = v.x; out.y = v.y; out.z = v.z;
  }

  public static readAngvel( body: RAPIER.RigidBody, out: { x: number; y: number; z: number } ): void
  {
    const p = this.cached( body );
    if ( p && !( p[14] & 2 ) )
    {
      out.x = p[11]; out.y = p[12]; out.z = p[13];
      return;
    }
    const v = body.angvel();
    out.x = v.x; out.y = v.y; out.z = v.z;
  }

  private static readContacts( data: Float64Array ): void
  {
    this.contacts.clear();
    let i = 0;
    while ( i < data.length )
    {
      const handle = data[i];
      const count = data[i + 1];
      i += 2;
      const list: ContactEntry[] = [];
      for ( let k = 0; k < count; k++ )
      {
        const other = data[i];
        const nx = data[i + 1], ny = data[i + 2], nz = data[i + 3];
        const n = data[i + 4];
        i += 5;
        const points: number[] = [];
        for ( let p = 0; p < n * 4; p++ )
          points.push( data[i + p] );
        i += n * 4;
        list.push( { other, nx, ny, nz, points } );
      }
      this.contacts.set( handle, list );
    }
  }

  private static replay(): void
  {
    for ( const cmd of this.cmds )
    {
      if ( cmd.o !== "b" || REPLAY_SKIP.has( cmd.m ) || this.poses.get( cmd.h )?.[STAMP] !== this.stamp )
        continue;
      const body = this.body( cmd.h );
      if ( !body )
        continue;
      this.body_orig[cmd.m].apply( body, cmd.a );
      this.noteBodyPose( body, cmd.m, cmd.a );
    }
  }

  private static refreshPoses(): void
  {
    const world = this.world!;
    this.ray_epoch++;
    this.poses.clear();
    world.forEachRigidBody( body =>
    {
      const t = body.translation();
      const r = body.rotation();
      const pose = new Float64Array( POSE_SIZE );
      pose[0] = body.handle;
      pose[1] = t.x; pose[2] = t.y; pose[3] = t.z;
      pose[4] = r.x; pose[5] = r.y; pose[6] = r.z; pose[7] = r.w;
      pose[14] = Number.NaN;
      this.poses.set( body.handle, pose );
    } );
  }

  private static noteBodyPose( body: RAPIER.RigidBody, name: string, args: unknown[] ): void
  {
    if ( name !== "setTranslation" && name !== "setRotation" )
      return;
    this.ray_epoch++;
    this.needs_propagate = true;
    const pose = this.poses.get( body.handle );
    const v = args[0] as { x: number; y: number; z: number; w?: number } | undefined;
    if ( !pose || !v )
      return;
    if ( name === "setTranslation" )
    {
      pose[1] = v.x; pose[2] = v.y; pose[3] = v.z;
    }
    else
    {
      pose[4] = v.x; pose[5] = v.y; pose[6] = v.z; pose[7] = v.w ?? 1;
    }
    pose[14] = Number.NaN;
  }

  private static usesWorker( world: RAPIER.World ): boolean
  {
    return this.active && !this.dirty && world === this.world;
  }

  private static install(): void
  {
    if ( this.installed )
      return;
    this.installed = true;

    const self = this;
    const body_proto = RAPIER.RigidBody.prototype as unknown as Methods;
    for ( const name of BODY_MUTATORS )
    {
      const orig = body_proto[name];
      if ( !orig )
        continue;
      this.body_orig[name] = orig;
      body_proto[name] = function ( this: RAPIER.RigidBody, ...args: unknown[] )
      {
        const mine = self.recording && self.ownsBody( this );
        if ( mine )
          self.ensureMirror( this );
        const result = orig.apply( this, args );
        self.ray_epoch++;
        if ( mine )
        {
          self.cmds.push( { o: "b", h: this.handle, m: name, a: args.map( copyArg ) } );
          const pose = self.poses.get( this.handle );
          if ( pose )
            pose[NATIVE] = 1;
          self.noteBodyPose( this, name, args );
        }
        return result;
      };
    }

    for ( const name of [ "translation", "rotation", "linvel", "angvel", "isSleeping" ] )
      this.read_orig[name] = body_proto[name];
    body_proto.translation = function ( this: RAPIER.RigidBody )
    {
      const p = self.cached( this );
      return p ? { x: p[1], y: p[2], z: p[3] } : self.read_orig.translation.call( this );
    };
    body_proto.rotation = function ( this: RAPIER.RigidBody )
    {
      const p = self.cached( this );
      return p ? { x: p[4], y: p[5], z: p[6], w: p[7] } : self.read_orig.rotation.call( this );
    };
    body_proto.linvel = function ( this: RAPIER.RigidBody )
    {
      const p = self.cached( this );
      return p && !( p[14] & 2 ) ? { x: p[8], y: p[9], z: p[10] } : self.read_orig.linvel.call( this );
    };
    body_proto.angvel = function ( this: RAPIER.RigidBody )
    {
      const p = self.cached( this );
      return p && !( p[14] & 2 ) ? { x: p[11], y: p[12], z: p[13] } : self.read_orig.angvel.call( this );
    };
    body_proto.isSleeping = function ( this: RAPIER.RigidBody )
    {
      const p = self.cached( this );
      return p ? ( p[14] & 1 ) === 1 : self.read_orig.isSleeping.call( this );
    };

    const collider_proto = RAPIER.Collider.prototype as unknown as Methods;
    const mass_setters = new Set( [ "setMass", "setDensity", "setMassProperties" ] );
    const shape_setters = new Set( [ "setTranslationWrtParent", "setHalfExtents", "setRadius", "setHalfHeight",
      "setRoundRadius", "setShape", "setTranslation", "setRotation", "setRotationWrtParent" ] );
    for ( const name of COLLIDER_MUTATORS )
    {
      const orig = collider_proto[name];
      if ( !orig )
        continue;
      this.collider_orig[name] = orig;
      collider_proto[name] = function ( this: RAPIER.Collider, ...args: unknown[] )
      {
        const mine = self.ownsCollider( this );
        const result = orig.apply( this, args );
        self.ray_epoch++;
        if ( !mine )
          return result;
        if ( self.recording )
          self.cmds.push( { o: "c", h: this.handle, m: name, a: args.map( copyArg ) } );
        if ( self.active && mass_setters.has( name ) )
        {
          const parent = this.parent();
          if ( parent )
            self.body_orig.recomputeMassPropertiesFromColliders.call( parent );
        }
        if ( shape_setters.has( name ) )
        {
          this.clearShapeCache();
          self.describe( this );
          self.needs_propagate = true;
        }
        return result;
      };
    }

    for ( const name of [ "translation", "rotation", "containsPoint", "projectPoint", "intersectsRay", "castRay",
      "castRayAndGetNormal", "castShape", "castCollider", "intersectsShape", "contactShape", "contactCollider" ] )
    {
      const orig = collider_proto[name];
      if ( !orig )
        continue;
      collider_proto[name] = function ( this: RAPIER.Collider )
      {
        if ( self.recording && self.ownsCollider( this ) )
        {
          const parent = this.parent();
          if ( parent )
            self.ensureMirror( parent );
          self.propagate();
        }
        return orig.apply( this, arguments as unknown as unknown[] );
      };
    }

    const world_proto = RAPIER.World.prototype as unknown as Methods;
    const debug_render = world_proto.debugRender;
    world_proto.debugRender = function ( this: RAPIER.World, ...args: unknown[] )
    {
      if ( self.recording && this === self.world )
        self.syncAll();
      return debug_render.apply( this, args );
    };
    for ( const name of [ "createRigidBody", "createCollider", "removeRigidBody", "removeCollider", "castRay",
      "castRayAndGetNormal", "contactPairsWith", "contactPair", "propagateModifiedBodyPositionsToColliders" ] )
      this.world_orig[name] = world_proto[name];

    world_proto.createRigidBody = function ( this: RAPIER.World, desc: unknown )
    {
      const body = self.world_orig.createRigidBody.call( this, desc ) as RAPIER.RigidBody;
      if ( this === self.world )
      {
        self.ray_epoch++;
        if ( self.recording )
          self.cmds.push( { o: "cb", h: body.handle, d: serializeBodyDesc( desc as RAPIER.RigidBodyDesc ) } );
        self.poses.delete( body.handle );
      }
      return body;
    };

    world_proto.createCollider = function ( this: RAPIER.World, desc: unknown, parent?: unknown )
    {
      const collider = self.world_orig.createCollider.call( this, desc, parent ) as RAPIER.Collider;
      if ( this === self.world )
      {
        if ( self.recording )
          self.cmds.push( {
            o: "cc", h: collider.handle,
            p: parent ? ( parent as RAPIER.RigidBody ).handle : null,
            d: serializeColliderDesc( desc as RAPIER.ColliderDesc ),
          } );
        self.describe( collider );
      }
      return collider;
    };

    world_proto.removeRigidBody = function ( this: RAPIER.World, body: unknown )
    {
      const rb = body as RAPIER.RigidBody;
      if ( this === self.world && !rb.isValid() )
        return;
      const handle = rb.handle;
      const colliders: number[] = [];
      if ( this === self.world )
        try
        {
          for ( let i = 0; i < rb.numColliders(); i++ )
            colliders.push( rb.collider( i ).handle );
        }
        catch {}

      const result = self.world_orig.removeRigidBody.call( this, body );
      if ( this === self.world )
      {
        if ( self.recording )
          self.cmds.push( { o: "rb", h: handle } );
        for ( const h of colliders )
          self.forget( h );
        self.poses.delete( handle );
      }
      return result;
    };

    world_proto.removeCollider = function ( this: RAPIER.World, collider: unknown, wake_up: unknown )
    {
      const c = collider as RAPIER.Collider;
      if ( this === self.world && !c.isValid() )
        return;
      const handle = c.handle;
      let parent: RAPIER.RigidBody | null = null;
      if ( this === self.world )
        try { parent = c.parent(); } catch {}

      const result = self.world_orig.removeCollider.call( this, collider, wake_up );
      if ( this === self.world )
      {
        if ( self.recording )
          self.cmds.push( { o: "rc", h: handle, w: wake_up as boolean } );
        self.forget( handle );
        if ( self.active && parent && self.body( parent.handle ) )
          self.body_orig.recomputeMassPropertiesFromColliders.call( parent );
      }
      return result;
    };

    world_proto.castRay = function ( this: RAPIER.World, ...args: unknown[] )
    {
      if ( !self.usesWorker( this ) )
        return self.world_orig.castRay.apply( this, args );
      return self.castRay( args, false );
    };

    world_proto.castRayAndGetNormal = function ( this: RAPIER.World, ...args: unknown[] )
    {
      if ( !self.usesWorker( this ) )
        return self.world_orig.castRayAndGetNormal.apply( this, args );
      return self.castRay( args, true );
    };

    world_proto.contactPairsWith = function ( this: RAPIER.World, collider: unknown, f: unknown )
    {
      if ( !self.usesWorker( this ) )
        return self.world_orig.contactPairsWith.call( this, collider, f );
      const handle = ( collider as RAPIER.Collider ).handle;
      self.track( handle );
      const list = self.contacts.get( handle );
      if ( !list )
        return;
      for ( const entry of list )
      {
        const other = self.collider( entry.other );
        if ( other )
          ( f as ( c: RAPIER.Collider ) => void )( other );
      }
    };

    world_proto.contactPair = function ( this: RAPIER.World, c1: unknown, c2: unknown, f: unknown )
    {
      if ( !self.usesWorker( this ) )
        return self.world_orig.contactPair.call( this, c1, c2, f );
      const h1 = ( c1 as RAPIER.Collider ).handle;
      const h2 = ( c2 as RAPIER.Collider ).handle;
      self.track( h1 );
      const callback = f as ( manifold: ContactView, flipped: boolean ) => void;
      const direct = self.contacts.get( h1 )?.find( entry => entry.other === h2 );
      if ( direct )
      {
        callback( new ContactView( direct, false ), false );
        return;
      }
      const reverse = self.contacts.get( h2 )?.find( entry => entry.other === h1 );
      if ( reverse )
        callback( new ContactView( reverse, true ), true );
    };

    for ( const key of WORLD_SETTERS )
    {
      const descriptor = Object.getOwnPropertyDescriptor( RAPIER.World.prototype, key );
      if ( !descriptor?.set || !descriptor.get )
        continue;
      const set = descriptor.set;
      Object.defineProperty( RAPIER.World.prototype, key, {
        configurable: true,
        get: descriptor.get,
        set( this: RAPIER.World, value: unknown )
        {
          set.call( this, value );
          if ( self.recording && this === self.world )
            self.cmds.push( { o: "w", k: key, v: value } );
        },
      } );
    }
  }

  private static track( handle: number ): void
  {
    if ( this.tracked.has( handle ) )
      return;
    this.tracked.add( handle );
    this.to_track.push( handle );
  }

  private static forget( handle: number ): void
  {
    this.ray_epoch++;
    this.meta.delete( handle );
    this.contacts.delete( handle );
    if ( this.tracked.delete( handle ) )
      this.to_untrack.push( handle );
  }

  private static describe( collider: RAPIER.Collider ): void
  {
    this.ray_epoch++;
    const parent = collider.parent();
    const offset = parent ? collider.translationWrtParent() : collider.translation();
    this.meta.set( collider.handle, {
      parent: parent ? parent.handle : null,
      ox: offset?.x ?? 0, oy: offset?.y ?? 0, oz: offset?.z ?? 0,
      radius: PhysicsThread.boundingRadius( collider ),
    } );
  }

  private static boundingRadius( collider: RAPIER.Collider ): number
  {
    const shape = collider.shape as unknown as Record<string, unknown>;
    const he = shape.halfExtents as { x: number; y: number; z: number } | undefined;
    const r = ( shape.radius as number | undefined ) ?? 0;
    const hh = ( shape.halfHeight as number | undefined ) ?? 0;
    const border = ( shape.borderRadius as number | undefined ) ?? 0;
    if ( he )
      return Math.hypot( he.x, he.y, he.z ) + border;
    const vertices = shape.vertices as Float32Array | undefined;
    if ( vertices )
    {
      let max = 0;
      for ( let i = 0; i < vertices.length; i += 3 )
        max = Math.max( max, Math.hypot( vertices[i], vertices[i + 1], vertices[i + 2] ) );
      return max + border;
    }
    if ( hh > 0 )
      return Math.hypot( hh, r ) + r + border;
    return r + border || 1e6;
  }

  private static center( meta: ColliderMeta, out: { x: number; y: number; z: number } ): void
  {
    if ( meta.parent === null )
    {
      out.x = meta.ox; out.y = meta.oy; out.z = meta.oz;
      return;
    }

    let px: number, py: number, pz: number, qx: number, qy: number, qz: number, qw: number;
    const pose = this.poses.get( meta.parent );
    if ( pose )
    {
      px = pose[1]; py = pose[2]; pz = pose[3];
      qx = pose[4]; qy = pose[5]; qz = pose[6]; qw = pose[7];
    }
    else
    {
      const body = this.body( meta.parent );
      if ( !body )
      {
        out.x = out.y = out.z = Number.NaN;
        return;
      }
      const t = body.translation();
      const r = body.rotation();
      px = t.x; py = t.y; pz = t.z; qx = r.x; qy = r.y; qz = r.z; qw = r.w;
    }

    const ix = qw * meta.ox + qy * meta.oz - qz * meta.oy;
    const iy = qw * meta.oy + qz * meta.ox - qx * meta.oz;
    const iz = qw * meta.oz + qx * meta.oy - qy * meta.ox;
    const iw = -qx * meta.ox - qy * meta.oy - qz * meta.oz;
    out.x = px + ix * qw + iw * -qx + iy * -qz - iz * -qy;
    out.y = py + iy * qw + iw * -qy + iz * -qx - ix * -qz;
    out.z = pz + iz * qw + iw * -qz + ix * -qy - iy * -qx;
  }

  private static rayCache(): number
  {
    if ( this.ray_cache_epoch === this.ray_epoch )
      return this.ray_count;

    const world = this.world!;
    const needed = world.colliders.len();
    if ( this.ray_handles.length < needed )
    {
      const size = Math.max( needed, this.ray_handles.length * 2, 64 );
      this.ray_handles = new Float64Array( size );
      this.ray_parents = new Float64Array( size );
      this.ray_cx = new Float64Array( size );
      this.ray_cy = new Float64Array( size );
      this.ray_cz = new Float64Array( size );
      this.ray_r = new Float64Array( size );
    }

    const c = this.center_tmp;
    let i = 0;
    world.forEachCollider( collider =>
    {
      const meta = this.meta.get( collider.handle );
      if ( !meta || i >= this.ray_handles.length )
        return;
      this.center( meta, c );
      this.ray_handles[i] = collider.handle;
      this.ray_parents[i] = meta.parent ?? -1;
      this.ray_cx[i] = c.x;
      this.ray_cy[i] = c.y;
      this.ray_cz[i] = c.z;
      this.ray_r[i] = meta.radius + RAY_MARGIN;
      i++;
    } );
    this.ray_count = i;
    this.ray_cache_epoch = this.ray_epoch;
    return i;
  }

  private static castRay( args: unknown[], with_normal: boolean ): unknown
  {
    const [ ray_arg, max_toi_arg, solid_arg, flags_arg, groups_arg, exclude_collider, exclude_body, predicate ] = args as [
      RAPIER.Ray, number, boolean, number | undefined, number | undefined, RAPIER.Collider | undefined, RAPIER.RigidBody | undefined,
      ( ( c: RAPIER.Collider ) => boolean ) | undefined,
    ];
    const candidates: RAPIER.Collider[] = [];

    const flags = flags_arg ?? 0;
    const o = ray_arg.origin;
    const d = ray_arg.dir;
    const a = d.x * d.x + d.y * d.y + d.z * d.z;
    let best_toi = max_toi_arg;
    let best: RAPIER.Collider | null = null;
    let best_normal: { x: number; y: number; z: number } | null = null;

    const count = this.rayCache();
    for ( let i = 0; i < count; i++ )
    {
      const handle = this.ray_handles[i];
      if ( exclude_collider && handle === exclude_collider.handle )
        continue;
      if ( exclude_body && this.ray_parents[i] === exclude_body.handle )
        continue;

      const cx = this.ray_cx[i];
      if ( !Number.isNaN( cx ) )
      {
        const radius = this.ray_r[i];
        const mx = o.x - cx, my = o.y - this.ray_cy[i], mz = o.z - this.ray_cz[i];
        const b = mx * d.x + my * d.y + mz * d.z;
        const cc = mx * mx + my * my + mz * mz - radius * radius;
        if ( cc > 0 )
        {
          const disc = b * b - a * cc;
          if ( disc < 0 || b > 0 )
            continue;
          const t0 = ( -b - Math.sqrt( disc ) ) / a;
          if ( t0 > best_toi )
            continue;
        }
      }

      const collider = this.collider( handle );
      if ( !collider )
        continue;
      if ( !collider.isEnabled() )
        continue;
      if ( flags !== 0 && !this.passesFlags( collider, flags ) )
        continue;
      if ( groups_arg !== undefined && !PhysicsThread.groupsMatch( collider.collisionGroups(), groups_arg ) )
        continue;
      if ( predicate && !predicate( collider ) )
        continue;

      candidates.push( collider );
    }

    for ( const collider of candidates )
    {
      const parent = collider.parent();
      if ( parent )
        this.ensureMirror( parent );
    }
    this.propagate();

    for ( const collider of candidates )
    {
      if ( with_normal )
      {
        const hit = collider.castRayAndGetNormal( ray_arg, best_toi, solid_arg );
        if ( hit && hit.timeOfImpact <= best_toi )
        {
          best_toi = hit.timeOfImpact;
          best = collider;
          best_normal = { x: hit.normal.x, y: hit.normal.y, z: hit.normal.z };
        }
      }
      else
      {
        const toi = collider.castRay( ray_arg, best_toi, solid_arg );
        if ( toi >= 0 && toi <= best_toi )
        {
          best_toi = toi;
          best = collider;
        }
      }
    }

    if ( !best )
      return null;
    return with_normal
      ? { collider: best, timeOfImpact: best_toi, normal: best_normal }
      : { collider: best, timeOfImpact: best_toi };
  }

  private static passesFlags( collider: RAPIER.Collider, flags: number ): boolean
  {
    const parent = collider.parent();
    const fixed = !parent || parent.isFixed();
    if ( ( flags & 1 ) && fixed ) return false;
    if ( ( flags & 2 ) && parent && parent.isKinematic() ) return false;
    if ( ( flags & 4 ) && parent && parent.isDynamic() ) return false;
    if ( ( flags & 8 ) && collider.isSensor() ) return false;
    if ( ( flags & 16 ) && !collider.isSensor() ) return false;
    return true;
  }

  private static groupsMatch( a: number, b: number ): boolean
  {
    return ( ( ( a >>> 16 ) & b ) & 0xffff ) !== 0 && ( ( ( b >>> 16 ) & a ) & 0xffff ) !== 0;
  }
}
